import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Sessions from '../Sessions';
import Traces from '../Traces';
import { palette, typography } from '../../utils/designTokens';
import { useEntities, useAppOverview, useActivity1h } from '../../services/appDetailApi';
import useFilterManager from '../../hooks/useFilterManager';
import { FilterPills } from '../../components';
import ExploreSearchField from '../../components/ExploreSearchField';
import { getRecentSearches, addRecentSearch } from '../../utils/recentSearches';
import { getSignalFilterDisplayLabel, resolveSignalFilterKey } from '../../utils/signalUtils';
import { useSearch } from '../../services/searchApi';
import { OUTCOME_VALUE_LABELS, OUTCOME_FILTER_HIDDEN_VALUES } from '../../utils/outcomeUtils';

/**
 * TelemetryTab - the "Explore" tab: page-level search over one result set,
 * projected as either Sessions or Traces via a view toggle.
 *
 * Owns shared semantic search + filter state and a count/delta line; the
 * Sessions/Traces toggle switches the projection without disturbing query
 * state. (Tools / Models moved to the Agents tab — see JourneysTab.)
 */
// Traces is the default view: traces are always present, sessions are
// conditional on session.id instrumentation. Explicit ?subtab=sessions in the
// URL still wins over this default (see activeSubTab below).
const SUB_TABS = [
  { key: 'traces', label: 'Traces' },
  { key: 'sessions', label: 'Sessions' },
];

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');

// Range param -> short human label for the totals line under the toggle.
const RANGE_LABELS = {
  '1h': 'last 1h', '24hours': 'last 24h', '7days': 'last 7d',
  '30days': 'last 30d', 'all-time': 'all time',
};

// Execution-outcome (tw.outcome_final) filter options — curated enum whose values
// match the backend's canonical outcome keys.
//
// '(none)' (label "Pending", the empty/unsealed outcome) is intentionally DROPPED for
// now: the list path (v2/traces_handler.py) maps the '(none)' sentinel → '' before the
// IN query, but the search path (search/serve.py) filters `tw.outcome_final = '(none)'`
// literally and matches nothing — so Pending would silently return empty in search mode.
// TODO(backend): make serve.py apply the same '(none)' → '' conversion, then re-add
// '(none)' here so the UI filter and the backend agree in both list and search modes.
// 'unknown' is a display-only fallback with no backing data.
const OUTCOME_FILTER_OPTIONS = Object.entries(OUTCOME_VALUE_LABELS)
  .filter(([value]) => value !== 'unknown' && value !== '(none)' && !OUTCOME_FILTER_HIDDEN_VALUES.has(value))
  .map(([value, label]) => ({ value, label }));

export default function TelemetryTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedAppId } = useParams();
  const activeSubTab = searchParams.get('subtab') || 'traces';

  // Tools/Models moved to Journeys. Redirect old deep links
  // (tab=telemetry&subtab=tools|models) to their new home so bookmarks/shares
  // keep working. Same subtab value, only the parent tab changes.
  useEffect(() => {
    const sub = searchParams.get('subtab');
    if (sub === 'tools' || sub === 'models') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'agents');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Shared search state
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [recents, setRecents] = useState(getRecentSearches);

  // Count + delta line: totals over the selected range + the last-1h deltas.
  const { appData } = useAppOverview(selectedAppId);
  const { traces1h, sessions1h } = useActivity1h(selectedAppId);
  const activity = appData?.kpis?.activity;

  const { results: searchResults, loading: searchLoading, search, clear: clearSearch } = useSearch(selectedAppId);

  const { entities: v2Entities } = useEntities(selectedAppId || null);

  // Entity lists for FilterPills options — populated via onEntitiesUpdate from children
  const [entities, setEntities] = useState({ agents: [], tools: [], models: [], workflows: [], users: [], sessions: [], signals: [] });
  const handleEntitiesUpdate = useCallback((update) => {
    setEntities(prev => ({
      ...prev,  // always preserve all keys — prevents undefined when new keys are added
      ...(update.agents?.length    && { agents:    update.agents }),
      ...(update.tools?.length     && { tools:     update.tools }),
      ...(update.models?.length    && { models:    update.models }),
      ...(update.workflows?.length && { workflows: update.workflows }),
      ...(update.users?.length     && { users:     update.users }),
      ...(update.sessions?.length  && { sessions:  update.sessions }),
      ...(update.signals?.length   && { signals:   update.signals }),
    }));
  }, []);

  // Filter state (owned here; passed down to Traces and Sessions)
  const {
    sessionIdFilter, agentFilter, toolFilter, modelFilter, workflowFilter,
    outcomeFilter,
    userFilter,
    addEntityFilter, removeEntityFilter,
  } = useFilterManager();

  // Signal filters live in URL query params (not in useFilterManager)
  const signalGroupFilter = searchParams.get('signal_group') || '';
  const signalFilter = searchParams.get('signal') || '';

  const handleAddFilter = useCallback((filterKey, value) => {
    if (filterKey === 'signal_group') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('signal_group', String(value).trim().toLowerCase());
      setSearchParams(newParams, { replace: true });
      return;
    }
    if (filterKey === 'signal') {
      const signalKey = resolveSignalFilterKey(String(value));
      if (signalKey) addEntityFilter('signal', signalKey);
      return;
    }
    addEntityFilter(filterKey, value);
  }, [addEntityFilter, searchParams, setSearchParams]);

  const handleRemoveFilter = useCallback((filterKey) => {
    if (filterKey === 'signal_group') {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('signal_group');
      setSearchParams(newParams, { replace: true });
      return;
    }
    removeEntityFilter(filterKey);
  }, [removeEntityFilter, searchParams, setSearchParams]);

  const getActiveFilters = useCallback(() => {
    const filters = [];
    if (sessionIdFilter) filters.push({ key: 'sessionId',       label: 'Session ID',   value: sessionIdFilter });
    if (userFilter)      filters.push({ key: 'user',            label: 'User ID',       value: userFilter });
    if (agentFilter)     filters.push({ key: 'agent',           label: 'Agent',         value: agentFilter });
    if (toolFilter)      filters.push({ key: 'tool',            label: 'Tool',          value: toolFilter });
    if (modelFilter)     filters.push({ key: 'model',           label: 'Model',         value: modelFilter });
    if (workflowFilter)  filters.push({ key: 'workflow',        label: 'Business Task', value: workflowFilter });
    if (outcomeFilter)          filters.push({ key: 'outcome',          label: 'Outcome',       value: OUTCOME_VALUE_LABELS[outcomeFilter] || outcomeFilter });
    if (signalGroupFilter) filters.push({ key: 'signal_group', label: 'Signal Group', value: signalGroupFilter });
    if (signalFilter)    filters.push({ key: 'signal', label: 'Signal', value: getSignalFilterDisplayLabel(signalFilter) || signalFilter });
    return filters;
  }, [sessionIdFilter, userFilter, agentFilter, toolFilter, modelFilter, workflowFilter, outcomeFilter, signalGroupFilter, signalFilter]);

  // Derive filter options from search results when in search mode,
  // otherwise use entity lists populated by the trace/session list fetches.
  const activeEntities = useMemo(() => {
    if (!searchResults) return entities;
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return {
      ...entities,
      workflows:       uniq(searchResults.map(r => r.workflow?.workflow_name || r.workflow_name)),
    };
  }, [searchResults, entities]);

  const getAvailableFilters = useCallback(() => {
    const available = [];
    // undefined options = free-text input (FilterPills shows these regardless)
    // non-empty array = dropdown suggestions
    if (!sessionIdFilter) available.push({ key: 'sessionId', label: 'Session ID', options: activeEntities.sessions.length  ? activeEntities.sessions  : undefined });
    if (!userFilter)      available.push({ key: 'user',      label: 'User ID',    options: activeEntities.users.length    ? activeEntities.users    : undefined });
    if (!agentFilter)     available.push({ key: 'agent',     label: 'Agent',      options: activeEntities.agents.length   ? activeEntities.agents   : undefined });
    if (!toolFilter)      available.push({ key: 'tool',      label: 'Tool',       options: activeEntities.tools.length    ? activeEntities.tools    : undefined });
    if (!modelFilter)     available.push({ key: 'model',     label: 'Model',      options: activeEntities.models.length   ? activeEntities.models   : undefined });
    if (!workflowFilter)  available.push({ key: 'workflow',  label: 'Business Task', options: activeEntities.workflows.length ? activeEntities.workflows : undefined });
    if (!outcomeFilter)         available.push({ key: 'outcome',          label: 'Outcome',       options: OUTCOME_FILTER_OPTIONS });
    return available;
  }, [sessionIdFilter, userFilter, agentFilter, toolFilter, modelFilter, workflowFilter, outcomeFilter, signalGroupFilter, signalFilter, activeEntities]);

  const filterValues = {
    sessionIdFilter, userFilter, agentFilter, toolFilter, modelFilter, workflowFilter,
    outcomeFilter,
    signalGroupFilter, signalFilter,
  };

  useEffect(() => {
    if (!v2Entities) return;
    setEntities(prev => ({
      ...prev,
      agents:    v2Entities.agents.length    ? v2Entities.agents    : prev.agents,
      tools:     v2Entities.tools.length     ? v2Entities.tools     : prev.tools,
      models:    v2Entities.models.length    ? v2Entities.models    : prev.models,
      workflows: v2Entities.workflows.length ? v2Entities.workflows : prev.workflows,
      sessions:  v2Entities.sessions.length  ? v2Entities.sessions  : prev.sessions,
      users:     v2Entities.users.length     ? v2Entities.users     : prev.users,
    }));
  }, [v2Entities]);

  // Execute search when activeSearch or filters change; delegate to useSearch hook
  useEffect(() => {
    if (!activeSearch) {
      clearSearch();
      return;
    }
    search(activeSearch, {
      ...(outcomeFilter  && { outcome: outcomeFilter }),
      ...(workflowFilter && { workflow_name: workflowFilter }),
    });
  }, [activeSearch, outcomeFilter, workflowFilter, search, clearSearch]);

  const handleSubTabChange = (key) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('subtab', key);
    setSearchParams(newParams);
  };

  const runSearch = useCallback((raw) => {
    const q = (raw ?? searchInput).trim();
    if (!q) return;
    setActiveSearch(q);
    setRecents(addRecentSearch(q));
  }, [searchInput]);

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    setActiveSearch('');
    clearSearch();
  }, [clearSearch]);

  const handlePickRecent = useCallback((q) => {
    setSearchInput(q);
    runSearch(q);
  }, [runSearch]);

  // Only disable Sessions once activity has LOADED and is known-zero — while
  // activity is null (loading) the segment stays enabled to avoid a
  // disabled→enabled flash for apps that do have sessions.
  const activityLoaded = activity != null;
  const noSessions = activityLoaded && activity.total_sessions === 0;

  const rangeLabel = RANGE_LABELS[searchParams.get('range') || '24hours'] || '';
  // Query outcome (distinct from range totals): only meaningful while a search is active.
  const resultCount = activeSearch && !searchLoading && Array.isArray(searchResults)
    ? searchResults.length
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* Row 1: the query surface (search + filters as ONE container) + Traces|Sessions toggle */}
      <div className="flex items-start gap-3 mb-3 flex-shrink-0">
        <ExploreSearchField
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => runSearch()}
          onClear={handleSearchClear}
          active={!!activeSearch}
          loading={searchLoading}
          recents={recents}
          onPickRecent={handlePickRecent}
          filterSlot={
            <FilterPills
              activeFilters={getActiveFilters()}
              availableFilters={getAvailableFilters()}
              onAddFilter={handleAddFilter}
              onRemoveFilter={handleRemoveFilter}
            />
          }
        />

        {/* Query outcome — sits with the search box, not with the range totals */}
        {resultCount != null && (
          <div className={`flex-shrink-0 ${typography.size.sm}`} style={{ color: palette.text.supporting, paddingTop: 18 }}>
            {fmt(resultCount)} result{resultCount === 1 ? '' : 's'}
          </div>
        )}

        {/* Right column: view toggle with the RANGE TOTALS directly beneath it */}
        <div className="flex flex-col items-end flex-shrink-0 ml-auto" style={{ gap: 6 }}>
        <div
          className="flex items-center rounded-lg border border-border-subtle overflow-hidden"
        >
          {SUB_TABS.map((tab, i) => {
            const isActive = tab.key === activeSubTab;
            const disabled = tab.key === 'sessions' && noSessions;
            return (
              <button
                key={tab.key}
                onClick={() => !disabled && handleSubTabChange(tab.key)}
                disabled={disabled}
                title={disabled ? 'No sessions in this range' : undefined}
                className={`px-3.5 py-1.5 text-sm font-medium transition-colors ${i > 0 ? 'border-l border-border-subtle' : ''} ${
                  isActive
                    ? 'bg-accent-primary-base text-text-inverse'
                    : disabled
                      ? 'text-text-muted cursor-not-allowed'
                      : 'text-text-supporting hover:text-text-primary'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Range totals — explicitly labeled with the window so they can't be
            mistaken for search results */}
        <div className={typography.size.xs} style={{ color: palette.text.muted, whiteSpace: 'nowrap' }}>
          {fmt(activity?.total_traces)} traces
          {traces1h > 0 && <span> (+{fmt(traces1h)} 1h)</span>}
          {' · '}
          {fmt(activity?.total_sessions)} sessions
          {sessions1h > 0 && <span> (+{fmt(sessions1h)} 1h)</span>}
          {rangeLabel && <span> · {rangeLabel}</span>}
        </div>
        </div>
      </div>

      {/* View content */}
      <div className="flex-1 min-h-0">
        {activeSubTab === 'sessions' && <Sessions searchResults={searchResults} filterValues={filterValues} onEntitiesUpdate={handleEntitiesUpdate} />}
        {activeSubTab === 'traces' && <Traces searchResults={searchResults} filterValues={filterValues} onAddFilter={handleAddFilter} onRemoveFilter={handleRemoveFilter} onEntitiesUpdate={handleEntitiesUpdate} />}
      </div>
    </div>
  );
}
